// src/pages/AdminDashboard.jsx

import React, { useEffect, useState } from 'react';
import {
  Users,
  KeyRound,
  ShieldCheck,
  ShieldOff,
  UserCheck,
} from 'lucide-react';

import { API_BASE } from '../../config';
import { useAuth } from '../../context/AuthContext';
import UsageTrendsTab from '../../components/tabs/UsageTrendsTab';
import AuditLogsTab from '../../pages/tabs/AuditLogsTab';

import './AdminDashboard.css';

const AdminDashboard = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState({});
  const [webhookFailures, setWebhookFailures] = useState([]);

 const fetchOverview = async () => {
  const res = await fetch(`${API_BASE}/admin/stats/overview`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!res.ok) {
    console.warn('Admin overview failed', res.status);

    if (res.status === 403) {
      // optional: show access denied UI
      return;
    }

    throw new Error(`Request failed: ${res.status}`);
  }

  const data = await res.json();
  setStats(data);
};


  const fetchWebhookFailures = async () => {
    const res = await fetch(`${API_BASE}/admin/webhook-failures`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await res.json();
    setWebhookFailures(data);
  };

  useEffect(() => {
    if (token) fetchOverview();
  }, [token]);

  useEffect(() => {
    if (activeTab === 'failures' && token) fetchWebhookFailures();
  }, [activeTab, token]);

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <h2>Admin Dashboard</h2>
      </div>

      <div className="tab-menu">
        {[
          ['overview', 'Overview'],
          ['usage', 'Usage Trends'],
          ['logs', 'Audit Logs'],
          ['failures', 'Webhook Failures'],
          ['manage', 'System Management'],
        ].map(([key, label]) => (
          <button
            key={key}
            className={activeTab === key ? 'active' : ''}
            onClick={() => setActiveTab(key)}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="tab-content">
        {activeTab === 'overview' && (
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon">
                <Users />
              </div>
              <div className="stat-meta">
                <span>Tenants</span>
                <br />
                <strong>{stats.totalTenants ?? 0}</strong>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">
                <KeyRound />
              </div>
              <div className="stat-meta">
                <span>API Keys</span>
                <br />
                <strong>{stats.totalApiKeys ?? 0}</strong>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon success">
                <ShieldCheck />
              </div>
              <div className="stat-meta">
                <span>Tokenizations</span>
                <br />
                <strong>{stats.totalTokenizations ?? 0}</strong>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon warning">
                <ShieldOff />
              </div>
              <div className="stat-meta">
                <span>Detokenizations</span>
                <br />
                <strong>{stats.totalDetokenizations ?? 0}</strong>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">
                <UserCheck />
              </div>
              <div className="stat-meta">
                <span>Active Users</span>
                <br />
                <strong>{stats.numberOfActiveUser ?? '—'}</strong>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'usage' && <UsageTrendsTab />}
        {activeTab === 'logs' && <AuditLogsTab />}

        {activeTab === 'failures' && (
          <div className="table-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0 }}>Webhook Dispatch Failures (Recent 20)</h3>
              <button
                className="btn btn-secondary"
                onClick={fetchWebhookFailures}
                style={{ fontSize: '0.85rem', cursor: 'pointer' }}
              >
                🔄 Refresh
              </button>
            </div>
            {webhookFailures && webhookFailures.length > 0 ? (
              <div className="table-responsive">
                <table className="admin-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)', textAlign: 'left' }}>
                      <th style={{ padding: '8px 12px' }}>Timestamp</th>
                      <th style={{ padding: '8px 12px' }}>Tenant ID</th>
                      <th style={{ padding: '8px 12px' }}>Destination URL</th>
                      <th style={{ padding: '8px 12px' }}>Error Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {webhookFailures.map((failure, idx) => (
                      <tr key={idx} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                        <td style={{ padding: '8px 12px' }}>
                          {failure.timestamp
                            ? new Date(failure.timestamp).toLocaleString()
                            : '—'}
                        </td>
                        <td style={{ padding: '8px 12px' }}>{failure.tenantId ?? '—'}</td>
                        <td style={{ padding: '8px 12px', maxWidth: '300px', wordBreak: 'break-all' }}>
                          <code>{failure.url}</code>
                        </td>
                        <td style={{ padding: '8px 12px', color: '#ef4444' }}>
                          {failure.error ?? 'Unknown error'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty-state" style={{ padding: '2.5rem', textAlign: 'center', color: '#10b981' }}>
                ✓ No recent webhook delivery failures. All webhook endpoints operating normally.
              </div>
            )}
          </div>
        )}

        {activeTab === 'manage' && (
          <div className="empty-state">
            System Management coming soon.
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
